import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import useAutoRefresh from './hooks/useAutoRefresh';

const API = process.env.REACT_APP_API_URL || 'https://ivision-crm-backend-production.up.railway.app';

const STATUS_FLOW = [
  { key: 'received', label: 'Received', icon: '📥', desc: 'Your vehicle has been checked in' },
  { key: 'diagnosed', label: 'Diagnosed', icon: '🔍', desc: 'Our tech has inspected your vehicle' },
  { key: 'approved', label: 'Approved', icon: '👍', desc: 'Work has been approved' },
  { key: 'in_progress', label: 'In Progress', icon: '🔧', desc: 'Work is underway' },
  { key: 'quality_check', label: 'Quality Check', icon: '✅', desc: 'Final inspection in progress' },
  { key: 'ready', label: 'Ready for Pickup', icon: '🏁', desc: 'Your vehicle is ready!' },
];

export default function TrackOrder() {
  const { orderNumber } = useParams();
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [history, setHistory] = useState([]);
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [manualCode, setManualCode] = useState('');

  const code = orderNumber || searchParams.get('code');

  useEffect(() => {
    if (code) fetchStatus(code);
    else setLoading(false);
  }, [code]);

  const fetchStatus = async (trackCode) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/public/track/${trackCode}`);
      const data = await res.json();
      if (!data.success) {
        setError(data.error || 'Not found');
        setOrder(null);
      } else {
        setOrder(data.order);
        setHistory(data.history || []);
        setShop(data.shop);
      }
    } catch (err) {
      setError('Unable to connect. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useAutoRefresh(function() {
    if (code && order) {
      fetch(API + '/api/public/track/' + code).then(function(r) { return r.json(); }).then(function(d) {
        if (d.success) { setOrder(d.order); setHistory(d.history || []); }
      }).catch(function() {});
    }
  }, 15000);

  const currentIdx = order ? STATUS_FLOW.findIndex(s => s.key === order.status) : -1;

  // ============================================================================
  // LOOKUP FORM (no code provided)
  // ============================================================================
  if (!code && !order) {
    return (
      <div style={S.page}>
        <div style={S.container}>
          <div style={S.logoSection}>
            <div style={S.logoIcon}>🏍️</div>
            <h1 style={S.mainTitle}>Where's My Bike?</h1>
            <p style={S.mainSubtitle}>Enter your work order number to check the status</p>
          </div>

          <div style={S.lookupCard}>
            <input
              style={S.lookupInput}
              value={manualCode}
              onChange={e => setManualCode(e.target.value.toUpperCase())}
              placeholder="WO-00001"
              maxLength={10}
            />
            <button
              onClick={() => manualCode && fetchStatus(manualCode)}
              style={S.lookupBtn}
              disabled={!manualCode}
            >
              Track My Vehicle
            </button>
          </div>

          {error && <div style={S.errorBox}>{error}</div>}
          {loading && <div style={S.loadingText}>Looking up your order...</div>}
        </div>
      </div>
    );
  }

  // ============================================================================
  // LOADING / ERROR
  // ============================================================================
  if (loading) {
    return (
      <div style={S.page}>
        <div style={S.container}>
          <div style={S.loadingText}>
            <div style={{fontSize: 48, marginBottom: 16}}>🔍</div>
            Looking up {code}...
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={S.page}>
        <div style={S.container}>
          <div style={S.logoSection}>
            <div style={S.logoIcon}>🏍️</div>
            <h1 style={S.mainTitle}>Where's My Bike?</h1>
          </div>
          <div style={S.errorBox}>{error}</div>
          <div style={S.lookupCard}>
            <input
              style={S.lookupInput}
              value={manualCode}
              onChange={e => setManualCode(e.target.value.toUpperCase())}
              placeholder="WO-00001"
              maxLength={10}
            />
            <button
              onClick={() => manualCode && fetchStatus(manualCode)}
              style={S.lookupBtn}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================================
  // STATUS PAGE
  // ============================================================================
  const isReady = order.status === 'ready';

  return (
    <div style={S.page}>
      <div style={S.container}>
        {/* Back link */}
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'12px 0'}}>
          <a href="/app" style={{color:'#a78bfa',fontSize:13,fontWeight:600,textDecoration:'none'}}>← Dashboard</a>
          {shop?.subdomain && <a href={'/site/' + shop.subdomain} style={{color:'#64748b',fontSize:12,fontWeight:600,textDecoration:'none'}}>View Site ↗</a>}
        </div>
        {/* Header */}
        <div style={S.logoSection}>
          {shop && <div style={S.shopName}>{shop.name}</div>}
          <div style={S.logoIcon}>{isReady ? '🏁' : '🏍️'}</div>
          <h1 style={S.mainTitle}>
            {isReady ? 'Your Ride is Ready!' : "Where's My Bike?"}
          </h1>
        </div>

        {/* Order Number + Vehicle */}
        <div style={S.orderCard}>
          <div style={S.orderNumber}>{order.order_number}</div>
          {order.customer_first_name && (
            <div style={S.customerName}>Hi {order.customer_first_name} 👋</div>
          )}
          {order.vehicle && (
            <div style={S.vehicleInfo}>
              <span style={S.vehicleBadge}>
                {order.vehicle.year} {order.vehicle.make} {order.vehicle.model}
              </span>
              {order.vehicle.color && (
                <span style={S.colorBadge}>{order.vehicle.color}</span>
              )}
            </div>
          )}
          {order.title && <div style={S.workTitle}>{order.title}</div>}
        </div>

        {/* STATUS TRACKER */}
        <div style={S.trackerCard}>
          <h3 style={S.sectionTitle}>Current Status</h3>
          <div style={S.statusTracker}>
            {STATUS_FLOW.map((step, i) => {
              const isComplete = i < currentIdx;
              const isCurrent = i === currentIdx;
              const isFuture = i > currentIdx;

              return (
                <div key={step.key} style={S.stepRow}>
                  {/* Connector line */}
                  {i > 0 && (
                    <div style={{
                      ...S.connector,
                      background: isComplete || isCurrent ? '#10b981' : '#334155',
                    }} />
                  )}

                  {/* Step circle */}
                  <div style={{
                    ...S.stepCircle,
                    background: isCurrent ? '#10b981' : isComplete ? '#10b981' : '#1e293b',
                    border: isCurrent ? '3px solid #10b981' : isComplete ? '3px solid #10b981' : '3px solid #334155',
                    boxShadow: isCurrent ? '0 0 20px rgba(16,185,129,0.4)' : 'none',
                    transform: isCurrent ? 'scale(1.15)' : 'scale(1)',
                  }}>
                    {isComplete ? '✓' : isCurrent ? step.icon : <span style={{opacity: 0.3}}>{step.icon}</span>}
                  </div>

                  {/* Label */}
                  <div style={{ flex: 1, marginLeft: 16 }}>
                    <div style={{
                      fontSize: isCurrent ? 16 : 14,
                      fontWeight: isCurrent ? 800 : isComplete ? 600 : 400,
                      color: isFuture ? '#475569' : '#f1f5f9',
                    }}>
                      {step.label}
                      {isCurrent && <span style={S.currentBadge}>CURRENT</span>}
                    </div>
                    {isCurrent && (
                      <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 4 }}>
                        {step.desc}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* DIAGNOSIS (if available) */}
        {order.diagnosis && (
          <div style={S.infoCard}>
            <h3 style={S.sectionTitle}>🔍 Diagnosis</h3>
            <p style={S.infoText}>{order.diagnosis}</p>
          </div>
        )}

        {/* DESCRIPTION */}
        {order.description && (
          <div style={S.infoCard}>
            <h3 style={S.sectionTitle}>📋 Work Description</h3>
            <p style={S.infoText}>{order.description}</p>
          </div>
        )}

        {/* ESTIMATED COMPLETION */}
        {order.estimated_completion && (
          <div style={S.infoCard}>
            <h3 style={S.sectionTitle}>📅 Estimated Completion</h3>
            <p style={{ ...S.infoText, fontSize: 18, fontWeight: 700 }}>
              {new Date(order.estimated_completion).toLocaleDateString('en-US', {
                weekday: 'long', month: 'long', day: 'numeric',
              })}
            </p>
          </div>
        )}

        {/* TIMELINE */}
        {history.length > 0 && (
          <div style={S.infoCard}>
            <h3 style={S.sectionTitle}>📜 Timeline</h3>
            {history.map((h, i) => {
              const step = STATUS_FLOW.find(s => s.key === h.status);
              return (
                <div key={i} style={S.timelineItem}>
                  <div style={S.timelineDot}>
                    {step?.icon || '•'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#f1f5f9' }}>
                      {step?.label || h.status}
                    </div>
                    {h.notes && <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 2 }}>{h.notes}</div>}
                    <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>
                      {new Date(h.timestamp).toLocaleDateString('en-US', {
                        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* CONTACT SHOP */}
        {shop && (
          <div style={S.contactCard}>
            <h3 style={{ ...S.sectionTitle, marginBottom: 12 }}>Questions?</h3>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
              {shop.phone && (
                <a href={`tel:${shop.phone}`} style={S.contactBtn}>
                  📞 Call {shop.name}
                </a>
              )}
              {shop.email && (
                <a href={`mailto:${shop.email}`} style={S.contactBtnAlt}>
                  ✉️ Email
                </a>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={S.footer}>
          Powered by IVision Marketing
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// STYLES
// ============================================================================
const S = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    padding: '20px 16px 40px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  container: { maxWidth: 480, margin: '0 auto' },

  logoSection: { textAlign: 'center', marginBottom: 24 },
  shopName: { fontSize: 13, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 8 },
  logoIcon: { fontSize: 56, marginBottom: 8 },
  mainTitle: { fontSize: 26, fontWeight: 800, color: '#f1f5f9', margin: '0 0 6px' },
  mainSubtitle: { fontSize: 14, color: '#94a3b8', margin: 0 },

  lookupCard: { background: '#1e293b', borderRadius: 16, padding: 24, border: '1px solid #334155', marginBottom: 16 },
  lookupInput: {
    width: '100%', padding: '14px 16px', borderRadius: 10, border: '2px solid #334155',
    background: '#0f172a', color: '#f1f5f9', fontSize: 20, fontWeight: 700,
    textAlign: 'center', letterSpacing: 3, fontFamily: 'monospace',
    outline: 'none', boxSizing: 'border-box', marginBottom: 12,
  },
  lookupBtn: {
    width: '100%', padding: '14px', borderRadius: 10, border: 'none',
    background: '#10b981', color: '#fff', fontSize: 16, fontWeight: 700,
    cursor: 'pointer',
  },

  errorBox: {
    background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12,
    padding: '14px 16px', color: '#dc2626', fontSize: 14, fontWeight: 500,
    textAlign: 'center', marginBottom: 16,
  },
  loadingText: { textAlign: 'center', color: '#94a3b8', fontSize: 16, padding: 40 },

  orderCard: {
    background: 'linear-gradient(135deg, #1e293b, #0f172a)',
    borderRadius: 16, padding: 24, border: '1px solid #334155',
    textAlign: 'center', marginBottom: 16,
  },
  orderNumber: {
    fontSize: 14, fontWeight: 800, color: '#10b981', letterSpacing: 3,
    fontFamily: 'monospace', marginBottom: 8,
  },
  customerName: { fontSize: 20, fontWeight: 700, color: '#f1f5f9', marginBottom: 12 },
  vehicleInfo: { display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 },
  vehicleBadge: {
    background: '#334155', color: '#e2e8f0', padding: '6px 14px',
    borderRadius: 8, fontSize: 14, fontWeight: 600,
  },
  colorBadge: {
    background: '#1e293b', color: '#94a3b8', padding: '6px 12px',
    borderRadius: 8, fontSize: 13, border: '1px solid #475569',
  },
  workTitle: { fontSize: 14, color: '#94a3b8', marginTop: 8 },

  trackerCard: {
    background: '#1e293b', borderRadius: 16, padding: 24,
    border: '1px solid #334155', marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 13, fontWeight: 700, color: '#64748b', textTransform: 'uppercase',
    letterSpacing: 1, marginBottom: 16, margin: '0 0 16px',
  },
  statusTracker: { position: 'relative' },
  stepRow: {
    display: 'flex', alignItems: 'center', position: 'relative',
    marginBottom: 8, minHeight: 48,
  },
  connector: {
    position: 'absolute', left: 22, top: -14, width: 3, height: 14, borderRadius: 2,
  },
  stepCircle: {
    width: 46, height: 46, borderRadius: '50%', display: 'flex',
    alignItems: 'center', justifyContent: 'center', fontSize: 18,
    flexShrink: 0, transition: 'all 0.3s', color: '#fff', fontWeight: 700,
  },
  currentBadge: {
    display: 'inline-block', background: '#10b981', color: '#fff',
    fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 4,
    marginLeft: 8, verticalAlign: 'middle',
  },

  infoCard: {
    background: '#1e293b', borderRadius: 16, padding: 20,
    border: '1px solid #334155', marginBottom: 16,
  },
  infoText: { fontSize: 14, color: '#cbd5e1', lineHeight: 1.6, margin: 0 },

  timelineItem: {
    display: 'flex', gap: 12, alignItems: 'flex-start',
    marginBottom: 14, paddingBottom: 14, borderBottom: '1px solid #1e293b',
  },
  timelineDot: {
    width: 36, height: 36, borderRadius: '50%', background: '#0f172a',
    border: '2px solid #334155', display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontSize: 14, flexShrink: 0,
  },

  contactCard: {
    background: 'linear-gradient(135deg, #064e3b, #065f46)',
    borderRadius: 16, padding: 24, textAlign: 'center', marginBottom: 16,
    border: '1px solid #10b981',
  },
  contactBtn: {
    display: 'inline-block', background: '#fff', color: '#065f46',
    padding: '12px 24px', borderRadius: 10, fontWeight: 700, fontSize: 14,
    textDecoration: 'none',
  },
  contactBtnAlt: {
    display: 'inline-block', background: 'transparent', color: '#fff',
    padding: '12px 24px', borderRadius: 10, fontWeight: 600, fontSize: 14,
    textDecoration: 'none', border: '1px solid rgba(255,255,255,0.3)',
  },

  footer: {
    textAlign: 'center', color: '#475569', fontSize: 12, marginTop: 24,
    fontWeight: 500,
  },
};
